import { ToastrService } from "ngx-toastr";
import { TranslateService } from "@ngx-translate/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { Observable, filter, finalize } from "rxjs";
import { Injectable, computed, inject, signal } from "@angular/core";

import {
  OutboxService,
  ReservationService,
  isNetworkError,
  localId,
  queueFailureMessage,
  reservationBodyForServer,
  reservationVisitorCount,
} from "src/app/core";

import { OfflineBookingBlock, OfflineBookingPolicyService } from "./offline-booking-policy.service";

/**
 * @description
 * Single submit path for every booking form.
 *
 * Each ticket form used to end with `service.addReservation(body).subscribe()` — no error callback.
 * A failed request therefore did nothing at all: no message, no navigation, and a form holding a
 * queue of visitors' details left in a state the clerk could not tell apart from success.
 *
 * Routing every form through here gives one place that owns the in-flight flag, the failure
 * message, and — from T-17 — the decision to queue a booking instead of failing it. That decision
 * belongs here and nowhere else: eight forms would be eight chances to get the eligibility rules
 * subtly different, and the rules are the safety mechanism.
 */
@Injectable({ providedIn: "root" })
export class BookingSubmitService {
  readonly #router = inject(Router);
  readonly #toastr = inject(ToastrService);
  readonly #outbox = inject(OutboxService);
  readonly #policy = inject(OfflineBookingPolicyService);
  readonly #translate = inject(TranslateService);
  readonly #reservationService = inject(ReservationService);

  /** A request is open. Cleared by `finalize`, so it says nothing about the queue write that may follow. */
  readonly #requesting = signal(false);

  /**
   * A booking is being written to the outbox. Tracked apart from `#requesting` because the queue
   * write *outlives* the request that triggered it: `finalize` clears `#requesting` as soon as the
   * failed request settles, while `#queueOrExplain` is still awaiting IndexedDB. One flag would go
   * back to `false` in that window and re-enable Confirm with the booking not yet on disk.
   */
  readonly #queueing = signal(false);

  /**
   * True while a booking is in flight — a request, a queue write, or a request that became one. The
   * ticket-form routes are siblings, so only one booking form is ever mounted; a single flag is
   * enough to keep every form's confirm button disabled and makes a double-click impossible to turn
   * into a second reservation. Offline that matters more, not less: nothing on screen moves while
   * the policy is evaluated, so a clerk who sees no response clicks again.
   */
  readonly isSubmitting = computed(() => this.#requesting() || this.#queueing());

  constructor() {
    // The flag lives on a root singleton, so a request that never settles — the offline counter
    // terminal this whole project is about — would leave every booking form's Confirm button
    // disabled, not just the one that submitted. Leaving the form abandons the submit, so clear it.
    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.#requesting.set(false);
        this.#queueing.set(false);
      });
  }

  /**
   * Submit through `ReservationService.addReservation`, which owns the navigation on success.
   *
   * Offline, the booking is queued instead — if every rule in `OfflineBookingPolicyService` agrees.
   * The check runs *before* the request when the network is already known to be down, so a clerk who
   * cannot book offline is told immediately rather than after a timeout, and the eligible case does
   * not spend a request it knows will fail.
   */
  submitReservation(body: Record<string, any>) {
    if (this.#policy.isOffline()) {
      // The same guard `#run` applies to the online path. Without it the offline branch is the one
      // route to the outbox with no double-click protection at all, and it is the branch where the
      // clerk sees the least happen — two clicks, two queued bookings, two reservations on sync.
      if (this.isSubmitting()) return;

      void this.#queueOrExplain(body);
      return;
    }

    this.#run(this.#reservationService.addReservation(body), undefined, () => void this.#queueOrExplain(body));
  }

  /**
   * Submit a booking form that needs the response, or that posts through its own service rather than
   * `addReservation`. Same in-flight and failure handling.
   *
   * **Nothing submitted this way is queued offline**, and three forms use it:
   *
   * - *tourism companies* and *friends of Oman museum* — visitor-facing applications rather than
   *   counter bookings, and T-17 scopes offline submit to the counter surface.
   * - *guided tours* — a counter TOUR booking, and therefore the one gap: it would satisfy every rule
   *   in `OfflineBookingPolicyService`, but it reads the reservation id out of the response to toast
   *   its own confirmation, which a queued booking cannot supply until T-18 lands the pending-ticket
   *   view. Until then it keeps the old behaviour rather than being queued with a broken success
   *   message. **If offline booking is switched on, this form still fails offline.**
   *
   * The old behaviour: a network failure is reported, and the form stays filled and re-submittable.
   */
  submitWith<T>(request: Observable<T>, onSuccess?: (value: T) => void) {
    this.#run(request, onSuccess);
  }

  #run<T>(request: Observable<T>, onSuccess?: (value: T) => void, onNetworkFailure?: () => void) {
    // A second click while the first request is still open would create a second booking.
    if (this.isSubmitting()) return;
    this.#requesting.set(true);

    request.pipe(finalize(() => this.#requesting.set(false))).subscribe({
      next: (value) => onSuccess?.(value),
      error: (error: unknown) => {
        // Only a request that never arrived may be queued. A 4xx is the server refusing this booking,
        // and queuing it would replay a refusal on a schedule while telling the clerk it was saved.
        if (onNetworkFailure && isNetworkError(error)) {
          onNetworkFailure();
          return;
        }

        this.#handleError(error);
      },
    });
  }

  /**
   * The offline branch: queue the booking, or say exactly why it cannot be queued.
   *
   * There is no silent middle. Either the clerk leaves with a booking that will send itself, or they
   * leave knowing it did not happen — the outcome this whole feature exists to prevent is the third
   * one, where they believe a sale went through and nothing did.
   */
  async #queueOrExplain(body: Record<string, any>) {
    // Held across the policy read *and* the write, so Confirm stays disabled for the whole of it.
    this.#queueing.set(true);

    try {
      // **Left set when the booking was queued**, and only then. The sale is on disk at that point and
      // this form is finished with: re-enabling Confirm on a form whose booking is already saved is an
      // invitation to take it twice, and a clerk looking at a filled-in form and a live button has no
      // way to know it is the same sale they just made. It normally clears a moment later anyway, when
      // the navigation to the pending ticket raises `NavigationEnd` — this is what holds the line in
      // the case where that navigation could not happen.
      //
      // Every path that leaves the booking unsaved clears it, because those forms *must* stay
      // submittable: the clerk still has a visitor to serve and nothing has been recorded.
      if (!(await this.#queue(body))) this.#queueing.set(false);
    } catch (error) {
      // `#queue` handles its own failures; anything reaching here is a bug in that handling rather
      // than a queue outcome. Clear the flag regardless — a stuck flag disables Confirm on every
      // booking form in the app, which is a worse failure than the one being reported.
      console.error("[booking] the queue path threw", error);
      this.#queueing.set(false);
    }
  }

  /** @returns true when the booking is on disk — the caller keeps Confirm disabled on the strength of it. */
  async #queue(body: Record<string, any>): Promise<boolean> {
    const decision = await this.#policy.evaluate(body);

    if (!decision.allowed) {
      this.#toastr.error(this.#translate.instant(blockedMessageKey(decision.block)));
      return false;
    }

    let clientId: string;

    try {
      // The same transform the online path applies, from the same function — a queued body has to be
      // what the server would have been sent, not an approximation of it.
      const payload = reservationBodyForServer({ ...body });

      clientId = await this.#outbox.enqueue({
        method: "POST",
        baseUrl: "reservation/",
        apiName: "add-reservation",
        body: payload,
        entity: "reservation",
        // The key for this booking attempt — the one the online request already used when there was
        // one, freshly minted when there was not. That request may have reached the server before the
        // connection died, so the queued replay must be recognisable as the same booking; and two
        // presses of Confirm on one form must be recognisable as each other. A key minted per enqueue
        // is how one booking becomes two reservations, by either route.
        idempotencyKey: this.#reservationService.idempotencyKeyForAttempt(),
        label: bookingLabel(payload, this.#translate),
      });

      this.#toastr.success(this.#translate.instant("SYNC.BOOKING_QUEUED"));
    } catch (error) {
      // `enqueue()` throws by design so this is reachable. A booking that reached neither the server
      // nor the queue is the one case where the clerk has to know immediately and re-take the sale.
      //
      // The message is chosen from the error rather than fixed (T-20): "check your connection and try
      // again" is the right advice for a storage fault and the wrong advice for a full disk, where the
      // connection is fine and trying again does exactly the same thing.
      console.error("[booking] could not queue the reservation", error);

      const message = queueFailureMessage(error);
      this.#toastr.error(this.#translate.instant(message.key, message.params));

      return false;
    }

    // Outside the `try` above on purpose. A navigation failure must not land in that `catch`, which
    // tells the clerk the booking was not saved — by this point it is on disk, and re-taking a sale
    // that is already queued is the double booking the idempotency key exists to prevent.
    await this.#openPendingTicket(clientId);

    return true;
  }

  /**
   * Open the pending ticket (T-18) for a booking that is already queued.
   *
   * **Awaited, and its result checked.** This used to be `void router.navigate(...)`, which fails
   * silently in precisely the conditions this path runs in: `PendingTicketComponent` is a
   * `loadComponent`, so offline its chunk has to come out of the service worker cache — and it is not
   * there at all under `ng serve`, where the worker is disabled (`app.config.ts`, `enabled:
   * environment.production`). The rejected promise went nowhere, and the clerk was left on a
   * filled-in booking form with the sale already on disk: indistinguishable, from where they are
   * standing, from Confirm having done nothing, and one more click away from a second queued booking.
   *
   * `localStorage.requestId` is deliberately **not** written here, unlike the online path.
   * `TicketComponent` falls back to it when it has no route parameter, and a local handle in that
   * slot would send a clerk into the real ticket view asking the server for an id it has never
   * issued. The pending page is reached by this navigation and by its URL, which is enough.
   *
   * The fallback is the booking services list, not the reservations list: a queued booking is on no
   * server, so the list that reads from one would open without their sale in it — the same false
   * impression by a longer route. It also happens to be a chunk this session has certainly loaded,
   * being where the clerk came from, so it can still open when the pending view could not. What it
   * cannot do is show the booking, so the toast says where that actually lives.
   */
  async #openPendingTicket(clientId: string): Promise<void> {
    const opened = await this.#router.navigate(["/admin/reservations/view", localId(clientId)]).catch((error: unknown) => {
      // A chunk that could not be fetched, or a guard that threw. Either way the entry is on disk.
      console.error("[booking] could not open the pending ticket", error);
      return false;
    });

    if (opened) return;

    this.#toastr.warning(this.#translate.instant("SYNC.BOOKING_QUEUED_NO_TICKET"));

    await this.#router.navigate(["/admin/booking-services"]).catch((error: unknown) => {
      // Nothing left to try. The booking is queued and the toast above has said so, which is the part
      // that matters; the clerk staying on the form is now a visible outcome rather than a silent one.
      console.error("[booking] could not leave the booking form", error);
      return false;
    });
  }

  #handleError(error: unknown) {
    // `errorInterceptor` already toasts 4xx and 5xx responses. A network-level failure carries
    // status 0, so none of its branches match and nothing is shown at all — that silence is the
    // case worth reporting here. Toasting unconditionally would double up on every server error.
    if (isNetworkError(error)) this.#toastr.error(this.#translate.instant("ALERT.NO_CONNECTION"));
  }
}

const blockedMessageKey = (block?: OfflineBookingBlock): string => {
  switch (block) {
    case "role":
      return "SYNC.OFFLINE_BOOKING_ROLE";
    case "notTour":
      return "SYNC.OFFLINE_BOOKING_NOT_TOUR";
    case "stalePricing":
      return "SYNC.OFFLINE_BOOKING_STALE_PRICING";
    default:
      // `disabled`, and anything a later rule adds without a message of its own. Deliberately the
      // plainest of the four: the clerk has done nothing wrong and there is nothing they can change.
      return "SYNC.OFFLINE_BOOKING_UNAVAILABLE";
  }
};

/**
 * What T-15's panel shows for a queued booking. Written now because it cannot be reconstructed later:
 * "POST reservation/add-reservation" tells a clerk with a visitor at the desk nothing at all, and the
 * contact name, date and party size are exactly what they would use to find the right one.
 */
const bookingLabel = (body: Record<string, any>, translate: TranslateService): string => {
  const contact = body["contactDetail"] as { name?: string } | undefined;
  const name = contact?.name || translate.instant("SYNC.BOOKING_UNNAMED");
  const date = String(body["visitDate"] ?? "").slice(0, 10);

  return translate.instant("SYNC.LABEL_BOOKING", { name, date, count: reservationVisitorCount(body) });
};
